import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { heroSlides as defaultSlides } from "../../../mocks/homeData";
import type { Slide } from "../../../api";

interface Props {
  slides?: Slide[];
}

/** 将 API Slide 转换为组件所需格式 */
function mapSlide(s: Slide) {
  return {
    id: String(s.id),
    title: s.title,
    subtitle: s.subtitle,
    description: s.description,
    cta: s.cta,
    ctaLink: s.ctaLink,
    image: s.image,
  };
}

export default function HeroSection({ slides: apiSlides }: Props) {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const slides = (apiSlides && apiSlides.length > 0)
    ? apiSlides.map(mapSlide)
    : defaultSlides;

  const goToSlide = useCallback((index: number) => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIndex(index);
      setIsTransitioning(false);
    }, 300);
  }, []);

  const handlePrev = useCallback(() => {
    const newIndex = currentIndex === 0 ? slides.length - 1 : currentIndex - 1;
    goToSlide(newIndex);
  }, [currentIndex, goToSlide, slides.length]);

  const handleNext = useCallback(() => {
    const newIndex = currentIndex === slides.length - 1 ? 0 : currentIndex + 1;
    goToSlide(newIndex);
  }, [currentIndex, goToSlide, slides.length]);

  useEffect(() => {
    const timer = setInterval(() => {
      handleNext();
    }, 6000);
    return () => clearInterval(timer);
  }, [handleNext]);

  const slide = slides[currentIndex];

  return (
    <section className="relative w-full h-[600px] md:h-[720px] overflow-hidden">
      {/* Background Slides */}
      {slides.map((s, index) => (
        <div
          key={s.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === currentIndex ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={s.image}
            alt={s.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
        </div>
      ))}

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col justify-center px-4 md:px-8 lg:px-12">
        <div className="max-w-4xl">
          {/* Trust Badge */}
          <div className="flex items-center gap-3 mb-6">
            <div className="flex -space-x-2">
              <div className="w-8 h-8 rounded-full border-2 border-white bg-gray-300 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20male%20business%20executive%20headshot%20portrait%20wearing%20navy%20blue%20suit%20with%20clean%20corporate%20background%20soft%20lighting%20neutral%20gray%20studio%20backdrop%20high%20quality%20photography&width=64&height=64&seq=21&orientation=squarish"
                  alt="Client"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="w-8 h-8 rounded-full border-2 border-white bg-gray-300 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Confident%20female%20industry%20leader%20professional%20headshot%20portrait%20wearing%20modern%20business%20attire%20natural%20lighting%20clean%20studio%20background%20high%20quality%20corporate%20photography%20elegant%20style&width=64&height=64&seq=22&orientation=squarish"
                  alt="Client"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="w-8 h-8 rounded-full border-2 border-white bg-gray-300 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Senior%20male%20technical%20engineer%20professional%20headshot%20portrait%20wearing%20smart%20casual%20with%20modern%20industrial%20office%20background%20high%20quality%20photography%20clean%20professional%20lighting%20style&width=64&height=64&seq=23&orientation=squarish"
                  alt="Client"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <span className="text-white/90 text-sm font-medium">
              Trusted by 500+ Industry Leaders
            </span>
          </div>

          {/* Title */}
          <h1
            className={`text-4xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-4 transition-all duration-500 ${
              isTransitioning ? "opacity-0 translate-y-4" : "opacity-100 translate-y-0"
            }`}
          >
            {slide.title}
            <br />
            <span className="font-light">{slide.subtitle}</span>
          </h1>

          {/* Description */}
          <p
            className={`text-white/80 text-base md:text-lg max-w-xl leading-relaxed mb-8 transition-all duration-500 delay-100 ${
              isTransitioning ? "opacity-0 translate-y-4" : "opacity-100 translate-y-0"
            }`}
          >
            {slide.description}
          </p>

          {/* CTA Button */}
          <div className="flex items-center gap-4">
            <a
              href={slide.ctaLink}
              onClick={(e) => {
                e.preventDefault();
                navigate(slide.ctaLink);
              }}
              className="inline-flex items-center gap-3 px-6 py-3 bg-white text-dark text-sm font-semibold rounded-full hover:bg-gray-100 transition-colors"
            >
              {slide.cta}
              <i className="ri-arrow-right-line text-lg"></i>
            </a>
          </div>
        </div>
      </div>

      {/* Slide Navigation */}
      <div className="absolute bottom-8 left-4 md:left-8 lg:left-12 z-10 flex items-center gap-4">
        {/* Arrows */}
        <button
          onClick={handlePrev}
          className="w-10 h-10 rounded-full border border-white/30 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
          aria-label="Previous slide"
        >
          <i className="ri-arrow-left-line"></i>
        </button>
        <button
          onClick={handleNext}
          className="w-10 h-10 rounded-full border border-white/30 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
          aria-label="Next slide"
        >
          <i className="ri-arrow-right-line"></i>
        </button>

        {/* Dots */}
        <div className="flex items-center gap-2 ml-4">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                index === currentIndex
                  ? "w-8 bg-white"
                  : "w-3 bg-white/40 hover:bg-white/60"
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Slide Counter */}
        <span className="text-white/60 text-sm font-medium ml-2">
          {String(currentIndex + 1).padStart(2, "0")} / {String(slides.length).padStart(2, "0")}
        </span>
      </div>
    </section>
  );
}